/**
 * The analysis contract: what a scoring plugin returns, and what it is given.
 *
 * The scoring logic lives in Python (`services/analysis/`) because it carries a
 * trained model. This file is the only place the two languages agree on, in both
 * directions: `bun run schemas:emit` turns it into JSON Schema that the Python
 * test suite validates its own output against, and the API re-parses every
 * response with these same schemas before persisting it.
 *
 * Two rules keep that working, and breaking either one is silent:
 *
 * 1. **Every date is `z.iso.datetime()`, never `z.coerce.date()`.** A `Date` has
 *    no JSON Schema representation, so `z.toJSONSchema` would either throw (it
 *    is configured to) or emit `{}`, which validates anything. The dashboard is
 *    unaffected: `lib/dates.ts` already formats `Date | string`.
 * 2. **No `.refine` / `.superRefine` anywhere in this file.** Refinements do not
 *    survive `z.toJSONSchema`, so an invariant written as one is invisible to
 *    Python and enforced on only one side. Everything here is structural.
 */
import { z } from "zod";

import { SurveyResponseSchema } from "./surveys";
import { TranslationMapSchema, patientGenderSchema } from "./utils";

/** Bumped only for a breaking reshape. Both sides assert it. */
export const ANALYSIS_CONTRACT_VERSION = 1 as const;

/* -------------------------------------------------------------------------- */
/*  Vocabulary                                                                */
/* -------------------------------------------------------------------------- */

/**
 * A finding's clinical severity, **already direction-resolved by the plugin**.
 *
 * This is the whole answer to the PROMIS trap: a T-score of 68 is `severe` on
 * Anxiety and `excellent` on Physical Function, and only the plugin knows which
 * domain it is looking at. The dashboard colours from this enum and never
 * inspects `higherIsBetter` to decide what red means.
 */
export const SeverityLevelSchema = z.enum([
  "excellent",
  "normal",
  "mild",
  "moderate",
  "severe",
  "unknown",
]);
export type SeverityLevel = z.infer<typeof SeverityLevelSchema>;

/** Closed, so a client can format a number without guessing at its meaning. */
export const AnalysisUnitSchema = z.enum(["tScore", "raw"]);
export type AnalysisUnit = z.infer<typeof AnalysisUnitSchema>;

/**
 * Why a block could not be computed. One i18n key per member on the dashboard,
 * mirroring the one-key-per-`ErrorCode` rule: never string-match the reason.
 */
export const UnavailableReasonSchema = z.enum([
  /** Nothing has been answered yet. */
  "NO_RESPONSE",
  /** Too many items missing from a domain to score it (skip logic, or a partial answer). */
  "INSUFFICIENT_ITEMS",
  /**
   * Fewer measurements than the finding needs — a trend over one point is a
   * dot, not a direction. Distinct from `NO_RESPONSE` on purpose: "answered
   * once, come back after the next questionnaire" and "never answered" are
   * different things to tell a clinician.
   */
  "INSUFFICIENT_TIMEPOINTS",
]);
export type UnavailableReason = z.infer<typeof UnavailableReasonSchema>;

/**
 * Which way a score has moved since the first measurement.
 *
 * Resolved by the PLUGIN, never by a client: it is already
 * direction-adjusted, so "improving" means clinically better whatever the
 * underlying number did. Exported so the dashboard imports this vocabulary
 * rather than re-declaring it as a local literal union.
 */
export const DeltaDirectionSchema = z.enum(["improving", "worsening", "stable"]);
export type DeltaDirection = z.infer<typeof DeltaDirectionSchema>;

/**
 * "Cannot be computed" is a STATE of a block, never a missing block.
 *
 * Nesting it here (rather than making `data` optional, or adding a
 * `notComputable` kind) is what makes omitting the reason a validation error,
 * and keeps the outer union flat on `kind` so the dashboard's block registry
 * stays a plain record. See CLAUDE.md for why silence is the wrong default.
 */
const body = <T extends z.ZodTypeAny>(data: T) =>
  z.discriminatedUnion("state", [
    z.object({ state: z.literal("ok"), data }),
    z.object({
      state: z.literal("unavailable"),
      reason: UnavailableReasonSchema,
    }),
  ]);

const blockBase = z.object({
  /** Unique within an envelope. The React key, and the target of cross-references. */
  key: z.string().min(1).max(64),
  titleKey: z.string(),
  descriptionKey: z.string().nullable(),
  /**
   * `null` means "this has no good or bad direction", a statement rather than an
   * omission. It drives direction affordances only (the delta arrow, the axis
   * caption); colour comes from `level`, which the plugin already resolved.
   */
  higherIsBetter: z.boolean().nullable(),
});

/* -------------------------------------------------------------------------- */
/*  Blocks                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The vocabulary is CLOSED, and only kinds with a dashboard component exist in
 * it. Adding a plugin is a Python-only change; adding a kind is deliberately a
 * three-workspace change, because it means a new way of showing something.
 *
 * Planned, added with the phase that renders them: `trend` and `benchmark`,
 * then `simulator` and `callout`, then `matrix` and `table`.
 */
export const AnalysisBlockSchema = z.discriminatedUnion("kind", [
  blockBase.extend({
    kind: z.literal("score"),
    body: body(
      z.object({
        value: z.number(),
        unit: AnalysisUnitSchema,
        /** The scale this sits on, for a gauge. Null when it is unbounded. */
        range: z.object({ min: z.number(), max: z.number() }).nullable(),
        level: SeverityLevelSchema,
        /** Against the previous occurrence. Null on a first submission. */
        delta: z
          .object({
            value: z.number(),
            /** Direction-resolved, like `level`: "improving" is never inferred client-side. */
            direction: DeltaDirectionSchema,
          })
          .nullable(),
      }),
    ),
  }),
  blockBase.extend({
    kind: z.literal("flag"),
    body: body(z.object({ level: SeverityLevelSchema })),
  }),
  blockBase.extend({
    kind: z.literal("trend"),
    body: body(
      z.object({
        /**
         * ONE axis, so one unit, one range and — via the block's own
         * `higherIsBetter` — one direction. A chart plotting anxiety beside
         * physical function is the PROMIS trap drawn at 400px: the two lines
         * cross and the reader concludes something that is not true. A plugin
         * with both polarities to show emits two blocks, which is why the
         * direction sits on the block rather than on each series.
         */
        unit: AnalysisUnitSchema,
        range: z.object({ min: z.number(), max: z.number() }).nullable(),
        /**
         * When each measurement was taken, oldest first. Instants, not
         * occurrence labels: the same questionnaire is routinely scheduled
         * twice in one study (a triggered baseline and a weekly series), so
         * "occurrence 0" names two different points and only a date orders them
         * against each other. The client formats them in its own locale.
         */
        points: z.array(z.iso.datetime()).min(2),
        series: z
          .array(
            z.object({
              key: z.string().min(1).max(64),
              labelKey: z.string(),
              /**
               * Positionally aligned to `points`, same length, `null` for a
               * measurement that could not be scored. Explicitly null rather
               * than absent: a missed occurrence is a GAP in the line, and a
               * shorter array would silently slide every later value one point
               * to the left — a chart that is wrong rather than incomplete.
               *
               * Length equality cannot be stated here (a `.refine` would not
               * survive `z.toJSONSchema`), so it is the emitting plugin's
               * invariant, pinned by its own tests.
               */
              values: z.array(z.number().nullable()),
            }),
          )
          .min(1),
      }),
    ),
  }),
]);
export type AnalysisBlock = z.infer<typeof AnalysisBlockSchema>;
export type AnalysisBlockKind = AnalysisBlock["kind"];

/**
 * Layout is expressed as SECTIONS and ORDER, and nothing else. The plugin says
 * "these belong together, in this sequence"; the dashboard decides what that
 * looks like at 1440px and at 390px, from `(role, kind)`.
 *
 * There is deliberately no `span`, `width`, `colour`, `chartType` or `icon`
 * anywhere in this contract. The moment Python describes pixels, dark mode,
 * translation and responsive layout all move to the Python release cycle.
 */
export const AnalysisSectionSchema = z.object({
  key: z.string(),
  titleKey: z.string(),
  /**
   * `decision-support` is structurally separate because a recommendation is not
   * a measurement: it renders in its own region, carries its own disclaimer, and
   * must never be mistaken for an observed value.
   */
  role: z.enum(["summary", "detail", "decision-support"]),
  blocks: z.array(AnalysisBlockSchema).min(1),
});
export type AnalysisSection = z.infer<typeof AnalysisSectionSchema>;

/** What produced a set of sections. Stored, so a number stays explainable later. */
export const AnalysisProvenanceSchema = z.object({
  surveyId: z.string(),
  pluginId: z.string(),
  pluginVersion: z.string(),
  modelVersion: z.string().nullable(),
  computedAt: z.iso.datetime(),
});
export type AnalysisProvenance = z.infer<typeof AnalysisProvenanceSchema>;

/**
 * `labelKey` -> text, for EVERY key any block or section references.
 *
 * Plugins emit keys rather than sentences so a cached result is
 * language-agnostic and one computation serves a French and an Arabic reader.
 * The envelope carries its own bundle so the dashboard never has to join
 * against a second request.
 */
const AnalysisLabelsSchema = z.record(z.string(), TranslationMapSchema);

/**
 * What ONE plugin call returns: one survey, scored by one package.
 *
 * This is the shape on the wire between the API and the analysis service, and
 * the shape the API caches. Its provenance is a single object because a plugin
 * scores exactly one questionnaire — the array lives in `AnalysisResultSchema`
 * below, which is a different thing assembled by a different process.
 *
 * Keeping them apart is not tidiness. When one schema described both, the API
 * had to index `provenance[0]` and carry a fallback for a case Python cannot
 * produce, and the cache had to reassemble a provenance object from separate
 * columns that nothing kept in step with this type.
 */
export const PluginAnalysisSchema = z.object({
  contractVersion: z.literal(ANALYSIS_CONTRACT_VERSION),
  provenance: AnalysisProvenanceSchema,
  sections: z.array(AnalysisSectionSchema),
  labels: AnalysisLabelsSchema,
});
export type PluginAnalysis = z.infer<typeof PluginAnalysisSchema>;

/**
 * One scored questionnaire's contribution to a study's analysis.
 *
 * Structurally what its plugin returned, plus the one thing Python cannot know:
 * what this deployment's catalog calls the questionnaire. Derived from
 * `PluginAnalysisSchema` rather than restated, so a field added to what a plugin
 * returns cannot go missing on the way to the dashboard.
 */
export const AnalysisSurveyResultSchema = PluginAnalysisSchema.omit({
  contractVersion: true,
}).extend({
  /** The catalog title. Patient-facing content, so a `TranslationMap`. */
  title: TranslationMapSchema,
});
export type AnalysisSurveyResult = z.infer<typeof AnalysisSurveyResultSchema>;

/**
 * One patient's analysis for one study, assembled by the API from one
 * `PluginAnalysis` per bound survey.
 *
 * Never produced by the analysis service, which knows nothing about studies.
 *
 * **Grouped by questionnaire, never concatenated.** Two instruments answered in
 * one study measure different things, so their findings do not belong in one
 * run of panels: the dashboard gives each questionnaire its own tab, with its
 * own provenance line. Flattening them cost more than it looked: section keys
 * had to be namespaced `${surveyId}:${key}` to stay unique, and the label
 * bundles were merged into one map where two plugins both shipping
 * `section.domains` silently resolved to whichever was assembled last.
 */
export const AnalysisResultSchema = z.object({
  contractVersion: z.literal(ANALYSIS_CONTRACT_VERSION),
  /**
   * One entry per scored questionnaire the study attaches, in the order the
   * study attached them. Empty means the study collects questionnaires that
   * nothing scores, which is a normal state and not an error.
   */
  surveys: z.array(AnalysisSurveyResultSchema),
});
export type AnalysisResult = z.infer<typeof AnalysisResultSchema>;

/* -------------------------------------------------------------------------- */
/*  What the API sends the analysis service                                   */
/* -------------------------------------------------------------------------- */

/**
 * One answered item. Keyed by the questionnaire's own identifiers
 * (`Question.key`, `Option.value`), never by a cuid and never by a position:
 * that is what lets a plugin be written once against the published instrument
 * and run against any facility's imported copy of it.
 *
 * `values` holds the response code for a SINGLE item, every selected code for a
 * MULTIPLE one, and the magnitude for a RANGE one. The plugin knows which of
 * its own items is which, so the type does not travel.
 */
export const AnalysisAnswerSchema = z.object({
  questionKey: z.string(),
  values: z.array(z.number()),
});

export const AnalysisSubmissionSchema = z.object({
  /**
   * When the patient answered. The list is already ordered by it, and it is the
   * x value of any longitudinal statistic.
   *
   * Nothing else about the occurrence travels: which scheduled survey produced
   * it, and its index within that one, are the API's business. A triggered
   * baseline and a weekly series are the same questionnaire at different times,
   * so a plugin sees one series and never has to know it was assembled.
   */
  submittedAt: z.iso.datetime(),
  answers: z.array(AnalysisAnswerSchema),
});

/**
 * The patient, pseudonymously. Sex and age are here because PROMIS normative
 * tables and essentially every clinical model are adjusted for both.
 *
 * Nothing else about a person crosses this boundary: no name, no phone, no
 * birthdate, no free text, and no patient id (`scopeId` names an enrolment or a plan, never a person).
 * A test asserts that.
 */
export const AnalysisSubjectSchema = z.object({
  sex: patientGenderSchema.nullable(),
  ageYears: z.number().int().nullable(),
});

export const AnalysisRequestSchema = z.object({
  contractVersion: z.literal(ANALYSIS_CONTRACT_VERSION),
  /**
   * A pseudonymous handle for the scope being scored: the enrolment for a
   * study's questionnaire, the plan for one a clinician scheduled for a patient
   * alone. It was `patientStudyId` while a study was the only owner, and the
   * rename is the honest half of that becoming untrue — no plugin reads it, it
   * exists so a request can be correlated in a log without carrying a patient.
   */
  scopeId: z.string(),
  surveyId: z.string(),
  plugin: z.string(),
  /** Which locales the label bundle must cover: a study's declared languages,
   *  or the questionnaire's own when there is no study. */
  languages: z.array(z.string()).min(1),
  subject: AnalysisSubjectSchema,
  /**
   * Every occurrence of this survey the patient has answered in this study, in
   * one chronological list, oldest first, regardless of which scheduled survey
   * produced it.
   */
  submissions: z.array(AnalysisSubmissionSchema),
});
export type AnalysisRequest = z.infer<typeof AnalysisRequestSchema>;

/* -------------------------------------------------------------------------- */
/*  The plugin manifest                                                       */
/* -------------------------------------------------------------------------- */

/**
 * What the analysis service advertises.
 *
 * A plugin is a self-contained package: the scoring code **and** the
 * questionnaire it scores, versioned and installed together. Nothing about the
 * questionnaire's shape travels, because nothing here needs to check it: the
 * service validates its own package at boot, and an uploaded file can never
 * bind a plugin.
 */
/**
 * The two licences that let an establishment use a scoring package, recorded in
 * the package and shown in Settings.
 *
 * They are genuinely different things. The **software** licence is Nordial's:
 * this facility may use this survey and its analysis pipeline, and another one
 * costs money. It is enforced by delivery — an unlicensed package is never sent,
 * so its absence is the enforcement. The **instrument** licence is the
 * questionnaire's own: PROMIS-29 cannot be used clinically or commercially
 * without a licence from HealthMeasures, the facility must hold one, and Nordial
 * supplies it bundled. That one is nobody here's to grant, which is exactly why
 * it has to be consultable rather than assumed.
 *
 * Everything is a display string, deliberately. These are references to legal
 * documents, not fields anything computes on, and a typo in a date must not be
 * able to take the plugin list down.
 */
export const AnalysisLicenceSchema = z.object({
  /** The establishment both licences name. */
  licensedTo: z.string(),
  instrument: z.object({
    name: z.string(),
    /** Who grants it. Never Nordial: this is the questionnaire's own licensor. */
    licensor: z.string(),
    /** The facility's licence number with that licensor, for an audit. */
    reference: z.string().nullable(),
    url: z.url().nullable(),
    /** `YYYY-MM-DD`, shown not enforced. */
    grantedAt: z.string().nullable(),
    expiresAt: z.string().nullable(),
    terms: z.string().nullable(),
  }),
  software: z
    .object({
      reference: z.string().nullable(),
      grantedAt: z.string().nullable(),
      expiresAt: z.string().nullable(),
    })
    .nullable(),
});
export type AnalysisLicence = z.infer<typeof AnalysisLicenceSchema>;

export const AnalysisPluginSchema = z.object({
  id: z.string(),
  /** Semver for the package as a whole: scoring and questionnaire together. */
  version: z.string(),
  modelVersion: z.string().nullable(),
  /**
   * Null when the package carries no `licence.json`. Normal in development, and
   * a real signal in production: it means nobody recorded what this
   * establishment is entitled to.
   */
  licence: AnalysisLicenceSchema.nullable(),
});
export type AnalysisPlugin = z.infer<typeof AnalysisPluginSchema>;

/**
 * A plugin as the dashboard sees it: the manifest plus whether its
 * questionnaire is in this deployment's catalog yet.
 *
 * `installedSurveyId` is null before the first install, and points at a
 * *superseded* row after a version bump — installing a new version creates a
 * new `Survey`, because the old one is frozen history for every study that used
 * it.
 */
export const AnalysisPluginStatusSchema = AnalysisPluginSchema.extend({
  installedSurveyId: z.string().nullable(),
  installedVersion: z.string().nullable(),
  /**
   * True when the catalog holds this package's version. Installing is keyed on
   * the version alone, so republishing a package means bumping it — the same
   * discipline any package registry enforces, and one fewer column than
   * hashing the questionnaire to detect an edit nobody declared.
   */
  upToDate: z.boolean(),
});
export type AnalysisPluginStatus = z.infer<typeof AnalysisPluginStatusSchema>;

/* -------------------------------------------------------------------------- */
/*  Route schemas                                                             */
/* -------------------------------------------------------------------------- */

/**
 * `surveyId` narrows the envelope to ONE questionnaire, and every link in the
 * dashboard passes it (2026-08-12): scores are read one instrument at a time,
 * and a scope-wide read would compute every scored questionnaire in it — each
 * of which can mean a model call — to render one. Omitted, the whole scope
 * comes back, which is what the tabbed page used to want.
 */
export const getPatientStudyAnalysisSchemas = {
  params: z.object({ patientStudyId: z.string() }),
  querystring: z.object({ surveyId: z.string().optional() }),
  response: { 200: AnalysisResultSchema },
};

/**
 * The same envelope for the questionnaires scheduled for a patient outside any
 * study. One read per SCOPE, and monitoring is one scope per patient — there is
 * no enrolment to key it on, and a page per questionnaire would be a page with
 * one tab.
 */
export const getPatientMonitoringAnalysisSchemas = {
  params: z.object({ patientId: z.string() }),
  querystring: z.object({ surveyId: z.string().optional() }),
  response: { 200: AnalysisResultSchema },
};

export const listAnalysisPluginsSchemas = {
  response: { 200: z.array(AnalysisPluginStatusSchema) },
};

/**
 * Installs a plugin's bundled questionnaire into the survey catalog.
 *
 * Idempotent on (plugin, version): a second call returns the same survey. A new
 * package version creates a NEW survey rather than editing the old one, which
 * is the only correct move — a study that has collected answers is frozen, and
 * its questions are referenced by id from every stored answer.
 */
export const installAnalysisPluginSchemas = {
  params: z.object({ pluginId: z.string() }),
  response: { 200: SurveyResponseSchema, 201: SurveyResponseSchema },
};
